/**
 * Voice search, through whichever tier this device can actually offer.
 *
 * ## Capability-detected, never assumed
 *
 * The requirement was that the television must never *depend* on a phone. So the order is
 * native first, and every tier is probed at runtime rather than inferred from the device
 * profile:
 *
 *   1. `tizenVoice`  the panel's own recogniser, where the platform exposes one.
 *   2. `webSpeech`   `SpeechRecognition` on PC and mobile.
 *   3. `none`        the on-screen keyboard, unchanged.
 *
 * A phone-driven tier belongs between 2 and 3 and is deliberately not here yet: it needs
 * the WebSocket channel from slice E, and stubbing it now would mean shipping a tier that
 * reports itself supported and then does nothing. `probeTier` is written so it slots in
 * without touching any caller.
 *
 * Samsung's voice support is genuinely inconsistent across panels and firmware — the same
 * model can have `webapis.voicecontrol` on one build and not the next — which is why this
 * asks the object rather than checking a model number. Where nothing answers, the tier is
 * `none` and the search box is simply a search box.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

export type VoiceTier = 'tizenVoice' | 'webSpeech' | 'none';

export interface VoiceSearch {
  /** Whether a microphone button should be drawn at all. */
  supported: boolean;
  tier: VoiceTier;
  listening: boolean;
  /** What has been heard so far, before the recogniser commits to it. */
  interim: string;
  error: string | null;
  start: () => void;
  stop: () => void;
}

/* -- the platform objects, hand-typed because neither ships types this app can use -- */

interface SpeechRecognitionLike {
  lang: string;
  continuous: boolean;
  interimResults: boolean;
  start(): void;
  stop(): void;
  abort(): void;
  onresult: ((event: SpeechRecognitionEventLike) => void) | null;
  onerror: ((event: { error?: string }) => void) | null;
  onend: (() => void) | null;
}

interface SpeechRecognitionEventLike {
  resultIndex: number;
  results: ArrayLike<ArrayLike<{ transcript: string }> & { isFinal: boolean }>;
}

interface TizenVoiceControl {
  /** Present on panels with the client available; absent is the common case. */
  setCommandList?(list: string[]): void;
  addVoiceCommandListener?(cb: (command: string) => void): number;
  removeVoiceCommandListener?(id: number): void;
}

declare global {
  interface Window {
    SpeechRecognition?: new () => SpeechRecognitionLike;
    webkitSpeechRecognition?: new () => SpeechRecognitionLike;
  }
}

function tizenVoiceControl(): TizenVoiceControl | null {
  const api = (window as unknown as { webapis?: { voicecontrol?: TizenVoiceControl } }).webapis
    ?.voicecontrol;
  // Presence of the namespace is not enough — the listener method is what this needs, and
  // some firmware exposes the object with nothing useful on it.
  return api && typeof api.addVoiceCommandListener === 'function' ? api : null;
}

function speechRecognition(): (new () => SpeechRecognitionLike) | null {
  return window.SpeechRecognition ?? window.webkitSpeechRecognition ?? null;
}

export function probeTier(): VoiceTier {
  if (typeof window === 'undefined') return 'none';
  if (tizenVoiceControl()) return 'tizenVoice';
  if (speechRecognition()) return 'webSpeech';
  return 'none';
}

export function useVoiceSearch({
  onResult,
  lang = 'tr-TR',
}: {
  onResult: (text: string) => void;
  lang?: string;
}): VoiceSearch {
  // Probed once. Nothing here can change mid-session, and re-probing would mean the
  // microphone button could appear and disappear under the user's finger.
  const tier = useMemo(probeTier, []);
  const [listening, setListening] = useState(false);
  const [interim, setInterim] = useState('');
  const [error, setError] = useState<string | null>(null);

  const recognitionRef = useRef<SpeechRecognitionLike | null>(null);
  const tizenListenerRef = useRef<number | null>(null);

  // Held in a ref so the callbacks below never need it as a dependency — an inline
  // handler from the caller would otherwise tear down and rebuild the recogniser on
  // every render, which on some engines aborts a recognition already in progress.
  const onResultRef = useRef(onResult);
  onResultRef.current = onResult;

  const stop = useCallback(() => {
    setListening(false);
    setInterim('');

    const recognition = recognitionRef.current;
    if (recognition) {
      recognitionRef.current = null;
      // `abort`, not `stop`: `stop` asks for a final result and fires `onresult` one more
      // time, so cancelling would still submit a search the user just cancelled.
      try {
        recognition.abort();
      } catch {
        /* already finished */
      }
    }

    const voice = tizenVoiceControl();
    if (voice && tizenListenerRef.current !== null) {
      try {
        voice.removeVoiceCommandListener?.(tizenListenerRef.current);
      } catch {
        /* the panel tore it down already */
      }
      tizenListenerRef.current = null;
    }
  }, []);

  const start = useCallback(() => {
    setError(null);
    setInterim('');

    if (tier === 'tizenVoice') {
      const voice = tizenVoiceControl();
      if (!voice) return;
      try {
        const id = voice.addVoiceCommandListener?.((command: string) => {
          setInterim('');
          setListening(false);
          onResultRef.current(command);
        });
        tizenListenerRef.current = id ?? null;
        setListening(true);
      } catch {
        setError('Sesli arama bu televizyonda kullanılamıyor');
        setListening(false);
      }
      return;
    }

    if (tier === 'webSpeech') {
      const Recognition = speechRecognition();
      if (!Recognition) return;

      const recognition = new Recognition();
      recognition.lang = lang;
      // One utterance, then stop. Continuous recognition on a search box means the
      // microphone stays open while the viewer reads the results.
      recognition.continuous = false;
      recognition.interimResults = true;

      recognition.onresult = (event) => {
        let text = '';
        let final = false;
        for (let i = event.resultIndex; i < event.results.length; i++) {
          // Guarded rather than asserted: `results` is an engine-supplied `ArrayLike`, and
          // trusting its length against a browser API this app hand-typed is how a voice
          // search throws on one Chrome build and not another.
          const result = event.results[i];
          if (!result) continue;
          text += result[0]?.transcript ?? '';
          if (result.isFinal) final = true;
        }
        if (final) {
          setInterim('');
          setListening(false);
          onResultRef.current(text.trim());
        } else {
          setInterim(text);
        }
      };

      recognition.onerror = (event) => {
        setListening(false);
        setInterim('');
        // `no-speech` and `aborted` are ordinary outcomes — the viewer changed their mind,
        // or said nothing. Reporting them as errors would put a red message on the screen
        // for something that is not wrong.
        if (event.error === 'no-speech' || event.error === 'aborted') return;
        setError(
          event.error === 'not-allowed'
            ? 'Mikrofon izni verilmedi'
            : 'Ses tanıma başarısız oldu',
        );
      };

      recognition.onend = () => setListening(false);

      recognitionRef.current = recognition;
      try {
        recognition.start();
        setListening(true);
      } catch {
        // Chrome throws if `start` is called while a previous recognition is still
        // winding down. Not worth surfacing: the next press works.
        setListening(false);
      }
    }
  }, [tier, lang]);

  /*
   * Teardown on unmount.
   *
   * Without it the microphone stays open after the viewer navigates away — on a phone that
   * is a recording indicator on a screen the user has left, which is the kind of thing
   * that costs trust rather than frames.
   */
  useEffect(() => stop, [stop]);

  return {
    supported: tier !== 'none',
    tier,
    listening,
    interim,
    error,
    start,
    stop,
  };
}
